/**
 * Daily Order — Lauf-Auswahl und Vorschau (Phase 1, rein lesend).
 * Pfade ohne `/api/`-Prefix: der Core-api-Client haengt ihn selbst an.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '@libero/core/services/api';
import { Button } from '@libero/core/components/ui/button';
import BereitsBestellt from '../components/BereitsBestellt';
import Historie from '../components/Historie';
import KennzahlenKarten from '../components/KennzahlenKarten';
import LieferantenTabelle from '../components/LieferantenTabelle';
import StartBereich from '../components/StartBereich';
import UnzugeordnetTabelle from '../components/UnzugeordnetTabelle';
import { TESTLAUF_KLASSE, vorschauPasst } from '../lauf';
import type { LaufInfo, Vorschau } from '../typen';

const GRUND_KEIN_TESTAUFTRAG = 'kein Testauftrag (Kunden-Mail keine freigegebene Testadresse @anstoss24.de) — wird vom Testlauf nicht angefasst';
const GRUND_NICHT_BESTAETIGT = 'Testauftrag nicht im Startdialog bestätigt — wird vom Testlauf nicht angefasst';
const GRUND_NICHT_IN_VORSCHAU = 'nicht in der bestätigten Vorschau — heute nicht bestellt, nächster Lauf';
const GRUND_REIHENFOLGE: Record<string, number> = {
  'Lieferauftrag vorhanden': 0, 'Artikeldaten fehlen': 1, 'kein Lieferant': 2, [GRUND_KEIN_TESTAUFTRAG]: 3,
  [GRUND_NICHT_BESTAETIGT]: 4, [GRUND_NICHT_IN_VORSCHAU]: 4,
};
const GRUND_KLASSE: Record<string, string> = {
  'Lieferauftrag vorhanden': 'bg-[#DAD9D9] text-[#1A1A1A]',
  'Artikeldaten fehlen': 'bg-yellow-100 text-yellow-900',
  'kein Lieferant': 'bg-white text-gray-700 border border-[#DAD9D9]',
  [GRUND_KEIN_TESTAUFTRAG]: 'bg-white text-gray-500 border border-dashed border-[#DAD9D9]',
  [GRUND_NICHT_BESTAETIGT]: 'bg-yellow-50 text-yellow-900 border border-dashed border-yellow-300',
  [GRUND_NICHT_IN_VORSCHAU]: 'bg-yellow-50 text-yellow-900 border border-dashed border-yellow-300',
};

const TYP_NAMEN: Record<number, string> = { 1: 'Auftrag', 5: 'Auftrag (5)', 2: 'Lieferauftrag' };

export default function MainPage() {
  const [laeufe, setLaeufe] = useState<LaufInfo[]>([]);
  const [laufId, setLaufId] = useState('');
  const [vorschau, setVorschau] = useState<Vorschau | null>(null);
  const [laedt, setLaedt] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [historieSchluessel, setHistorieSchluessel] = useState(0);
  /** Ein Lauf startet oder läuft (aus StartBereich): Lauf-Auswahl gesperrt (WR-03). */
  const [laufAktiv, setLaufAktiv] = useState(false);
  /** Aktuell gewählter Lauf, damit eine verspätete Vorschau-Antwort zu einem anderen Lauf verworfen wird. */
  const gewaehltRef = useRef('');
  gewaehltRef.current = laufId;
  const meldeLaufAktiv = useCallback((aktiv: boolean) => setLaufAktiv(aktiv), []);
  const gewaehlt = laeufe.find((l) => l.id === laufId);
  const status = gewaehlt?.quellStatus ?? 5.2;

  useEffect(() => {
    api
      .get('/programmes/daily-order/laeufe')
      .then((res) => {
        const liste: LaufInfo[] = res.data?.data ?? [];
        setLaeufe(liste);
        if (liste.length > 0) setLaufId(liste[0].id);
      })
      .catch((err) => setFehler(err.response?.data?.error?.message ?? 'Läufe konnten nicht geladen werden'));
  }, []);

  const ladeVorschau = async () => {
    const angefragt = laufId;
    setLaedt(true);
    setFehler(null);
    try {
      const res = await api.post('/programmes/daily-order/vorschau', { laufId: angefragt });
      // WR-03: nur übernehmen, wenn die Antwort zum (noch) gewählten Lauf gehört.
      const daten: Vorschau | undefined = res.data.data;
      if (daten && vorschauPasst(daten, angefragt, gewaehltRef.current)) setVorschau(daten);
    } catch (err: any) {
      setVorschau(null);
      setFehler(err.response?.data?.error?.message ?? 'Vorschau konnte nicht geladen werden');
    } finally {
      setLaedt(false);
    }
  };

  return (
    <div className="p-6 space-y-4">
      <h1 className="text-2xl font-bold text-[#1A1A1A]">Daily Order</h1>
      <p className="text-sm text-gray-600">
        Vorschau ist rein lesend. ‚Lauf starten‘ verschickt die Bestellungen per Mail und setzt danach die bestellten Aufträge auf den Zielstatus (5.2 → 5.3 mit Markierung und Tags, 5.21 → 5.31 mit Markierung).
      </p>

      <div className="flex items-center gap-3">
        <select
          className="border border-[#DAD9D9] rounded px-3 py-2 text-sm"
          value={laufId}
          // WR-03: während eine Vorschau lädt oder ein Lauf läuft, ist der Lauf fest.
          disabled={laedt || laufAktiv}
          onChange={(e) => {
            setLaufId(e.target.value);
            // Vorschau gehört zu genau einem Lauf: beim Wechsel verwerfen, sonst startet der Knopf den falschen.
            setVorschau(null);
          }}
        >
          {laeufe.map((l) => (
            <option key={l.id} value={l.id}>
              {l.name}
            </option>
          ))}
        </select>
        <Button
          onClick={ladeVorschau}
          disabled={laedt || laufId === ''}
          className="bg-[#E9FF57] text-[#1A1A1A] hover:bg-[#d4ea4d]"
        >
          {laedt ? 'Lädt...' : 'Vorschau laden'}
        </Button>
      </div>

      {gewaehlt?.testlauf && (
        <div className={`p-3 rounded text-sm ${TESTLAUF_KLASSE}`}>
          TESTLAUF: Mails gehen nur an die Testadresse. Bestellt und geschrieben werden nur Testaufträge (Kunden-Mail eine
          freigegebene Testadresse @anstoss24.de), die beim Start im Dialog angezeigt sind. Alle anderen Aufträge in Status{' '}
          {String(status)} werden nicht angefasst.
        </div>
      )}

      {fehler && <div className="p-3 rounded bg-red-50 text-red-800 text-sm">{fehler}</div>}

      {vorschau && (
        <div className="space-y-6">
          <p className="text-sm text-gray-600">
            {vorschau.lauf.name}: geladen am {new Date(vorschau.geladenAm).toLocaleString('de-DE')}{' '}
            <span
              className="ml-2 px-2 py-0.5 rounded bg-[#E9FF57] text-[#1A1A1A] font-medium"
              title="Markierung nach Wochentag (Berliner Zeit), ISO-Kalenderwoche — so steht es in der adidas-Datei und später im Mail-Betreff"
            >
              Referenz: {vorschau.referenz.text}
            </span>
          </p>
          <StartBereich
            vorschau={vorschau}
            onLaufFertig={() => setHistorieSchluessel((n) => n + 1)}
            onLaeuftAenderung={meldeLaufAktiv}
          />
          {vorschau.warnungen.map((w) => (
            <div key={w} className="p-3 rounded bg-yellow-50 text-yellow-900 text-sm">
              {w}
            </div>
          ))}

          {vorschau.kennzahlen.auftraegeGesamt === 0 ? (
            <p className="text-sm text-gray-500">Keine Aufträge in Status {String(status)}.</p>
          ) : (
            <>
              <KennzahlenKarten
                kennzahlen={vorschau.kennzahlen}
                namen={Object.fromEntries(vorschau.lieferanten.map((l) => [l.id, l.name]))}
                quellStatus={status}
              />
              <BereitsBestellt vorschau={vorschau} />
              {vorschau.lieferanten.map((block) => (
                <LieferantenTabelle key={block.id} block={block} laufId={vorschau.lauf.id} versand={vorschau.versand} />
              ))}

              <UnzugeordnetTabelle positionen={vorschau.unzugeordnet} />

              <section className="space-y-2">
                <h2 className="text-lg font-semibold text-[#1A1A1A]">Bleibt in Status {String(status)}</h2>
                {vorschau.bleibtInStatus.length === 0 ? (
                  <p className="text-sm text-gray-500">Alle Aufträge werden bestellt.</p>
                ) : (
                  <table className="min-w-[400px] text-sm border border-[#DAD9D9]">
                    <thead className="bg-[#DAD9D9] text-[#1A1A1A]">
                      <tr>
                        <th className="px-3 py-2 text-left">Auftrag</th>
                        <th className="px-3 py-2 text-left">Typ</th>
                        <th className="px-3 py-2 text-left">Grund</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...vorschau.bleibtInStatus]
                        .sort((x, y) => (GRUND_REIHENFOLGE[x.grund] ?? 9) - (GRUND_REIHENFOLGE[y.grund] ?? 9) || x.orderId - y.orderId)
                        .map((b) => (
                        <tr key={b.orderId} className="border-t border-[#DAD9D9]">
                          <td className="px-3 py-2">{b.orderId}</td>
                          <td className="px-3 py-2">{TYP_NAMEN[b.typeId] ?? b.typeId}</td>
                          <td className="px-3 py-2">
                            <span className={`px-2 py-0.5 rounded text-xs ${GRUND_KLASSE[b.grund] ?? ''}`}>{b.grund}</span>
                            {b.detail && <span className="ml-2 text-xs text-gray-600">{b.detail}</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </section>
            </>
          )}
        </div>
      )}

      <Historie neuLadenSchluessel={historieSchluessel} />
    </div>
  );
}
